import { useMemo, useState } from 'react';
import {
  PACKAGING_QUANTIZATIONS,
  PACKAGING_RUNTIMES,
  derivePackagingRecommendation,
  roleAtLeast,
  type OrgRole,
  type PackagingQuantization,
  type PackagingRuntime,
  type PackagingStatus,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import { NumberInput } from '@carbon/react/es/components/NumberInput/NumberInput';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Play from '@carbon/icons-react/es/Play';
import TrashCan from '@carbon/icons-react/es/TrashCan';
import Download from '@carbon/icons-react/es/Download';

import { ApiError, apiFetch } from '../../api/client.js';
import { useAuthStore } from '../../auth/store.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { HelpHint } from '../../components/HelpHint.js';
import { usePackages } from './use-packages.js';

/** Libellés i18n des runtimes / quantifications, pour peupler les sélecteurs. */
const RUNTIME_LABEL: Record<PackagingRuntime, MessageKey> = {
  ONNX: 'deploy.packaging.runtime.onnx',
  TFLITE: 'deploy.packaging.runtime.tflite',
  TENSORRT: 'deploy.packaging.runtime.tensorrt',
};
const QUANT_LABEL: Record<PackagingQuantization, MessageKey> = {
  NONE: 'deploy.packaging.quant.none',
  FP16: 'deploy.packaging.quant.fp16',
  INT8: 'deploy.packaging.quant.int8',
};
const STATUS_META: Record<PackagingStatus, { cls: string; key: MessageKey }> = {
  DRAFT: { cls: 'draft', key: 'deploy.packaging.status.draft' },
  QUEUED: { cls: 'queued', key: 'deploy.packaging.status.queued' },
  BUILDING: { cls: 'building', key: 'deploy.packaging.status.building' },
  READY: { cls: 'ready', key: 'deploy.packaging.status.ready' },
  FAILED: { cls: 'failed', key: 'deploy.packaging.status.failed' },
};

/**
 * Sous-partie « Packaging » de la page Deploy (ADR-0034). On empaquette un modèle **validé** pour le
 * runtime cible d'un matériel : choix du modèle, de la cible (dont on lit l'enveloppe **calcul**, seule
 * à adapter le modèle — l'airframe est exclu par construction), puis recommandation dérivée
 * (surchargeable) et création. La construction est **dormante** : « Construire » signale honnêtement
 * l'état 503 tant que `PACKAGING_ENABLED` est faux. Le binaire se télécharge par URL pré-signée.
 */
export function PackagingSection({ anchorId = 'deploy-packaging' }: { anchorId?: string } = {}) {
  const t = useT();
  const role = useAuthStore((s) => s.currentUser?.memberships[0]?.role ?? null);
  const canEdit = role !== null && roleAtLeast(role as OrgRole, 'EDITOR');

  const { packages, models, assets, loading, error, create, launch, remove } = usePackages();

  // Seuls les modèles validés sont empaquetables (ADR-0034 §1).
  const readyModels = useMemo(() => models.filter((m) => m.status === 'READY'), [models]);

  const [modelId, setModelId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [runtime, setRuntime] = useState<PackagingRuntime>('ONNX');
  const [quantization, setQuantization] = useState<PackagingQuantization>('NONE');
  const [inputSize, setInputSize] = useState(640);
  // `true` quand l'utilisateur a explicitement surchargé la reco : on cesse alors de la réappliquer.
  const [overridden, setOverridden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [dormant, setDormant] = useState(false);

  const target = assets.find((a) => a.id === targetId) ?? null;
  const recommendation = target ? derivePackagingRecommendation(target.compute) : null;

  // Choisir une cible réinitialise la reco (sauf si l'utilisateur a déjà surchargé sciemment).
  const pickTarget = (id: string) => {
    setTargetId(id);
    const asset = assets.find((a) => a.id === id);
    if (asset && !overridden) {
      const rec = derivePackagingRecommendation(asset.compute);
      setRuntime(rec.runtime);
      setQuantization(rec.quantization);
    }
  };

  const submit = async () => {
    if (!modelId) return;
    setBusy(true);
    setFormError(null);
    try {
      await create({
        modelId,
        targetAssetId: targetId || null,
        runtime,
        quantization,
        inputSize,
      });
      setModelId('');
      setTargetId('');
      setOverridden(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('deploy.packaging.createError'));
    } finally {
      setBusy(false);
    }
  };

  const build = async (id: string) => {
    setFormError(null);
    setDormant(false);
    try {
      await launch(id);
    } catch (err) {
      // Packaging dormant : le gate répond 503. On l'affiche comme une info, pas une erreur.
      if (err instanceof ApiError && err.status === 503) setDormant(true);
      else setFormError(err instanceof Error ? err.message : t('deploy.packaging.buildError'));
    }
  };

  const download = async (id: string) => {
    setFormError(null);
    try {
      const { url } = await apiFetch<{ url: string }>(`/packages/${id}/artifact`);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('deploy.packaging.downloadError'));
    }
  };

  return (
    <section className="ddp-pkg" id={anchorId} aria-label={t('deploy.packaging.title')}>
      <div className="ddp-pkg__head">
        <h2 className="ddp-deploy__family-name">{t('deploy.packaging.title')}</h2>
        <HelpHint textKey="help.deploy.packaging" />
      </div>
      <p className="ddp-deploy__family-text">{t('deploy.packaging.lead')}</p>

      <div className="ddp-pkg__dormant">
        <span>{t('deploy.packaging.dormantNote')}</span>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
      {formError && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={formError}
          lowContrast
          onCloseButtonClick={() => setFormError(null)}
        />
      )}
      {dormant && (
        <InlineNotification
          kind="info"
          title={t('deploy.packaging.dormantTitle')}
          subtitle={t('deploy.packaging.dormantBody')}
          lowContrast
          onCloseButtonClick={() => setDormant(false)}
        />
      )}

      {canEdit && (
        <div className="ddp-pkg__form">
          <div className="ddp-pkg__grid">
            <Select
              id="pkg-model"
              labelText={t('deploy.packaging.field.model')}
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
            >
              <SelectItem value="" text={t('deploy.packaging.field.modelPlaceholder')} />
              {readyModels.map((m) => (
                <SelectItem key={m.id} value={m.id} text={`${m.name} · ${m.version}`} />
              ))}
            </Select>
            <Select
              id="pkg-target"
              labelText={t('deploy.packaging.field.target')}
              value={targetId}
              onChange={(e) => pickTarget(e.target.value)}
            >
              <SelectItem value="" text={t('deploy.packaging.field.targetNone')} />
              {assets.map((a) => (
                <SelectItem key={a.id} value={a.id} text={a.name} />
              ))}
            </Select>
          </div>

          {readyModels.length === 0 && (
            <p className="ddp-pkg__hint">{t('deploy.packaging.noReadyModel')}</p>
          )}

          {/* Enveloppe calcul de la cible (ADR-0034 §5 : seule elle adapte le modèle, jamais l'airframe). */}
          {target && (
            <div className="ddp-pkg__compute">
              <span className="ddp-pkg__compute-label">
                {t('deploy.packaging.computeEnvelope')}
              </span>
              <div className="ddp-pkg__compute-specs">
                {[
                  target.compute.soc,
                  target.compute.accelerator,
                  target.compute.tops != null ? `${target.compute.tops} TOPS` : null,
                  target.compute.latencyMs != null ? `${target.compute.latencyMs} ms` : null,
                ]
                  .filter((s): s is string => Boolean(s))
                  .map((s) => (
                    <span key={s} className="ddp-pkg__compute-spec">
                      {s}
                    </span>
                  ))}
                {!target.compute.soc &&
                  !target.compute.accelerator &&
                  target.compute.tops == null &&
                  target.compute.latencyMs == null && (
                    <span className="ddp-pkg__compute-spec ddp-pkg__compute-spec--empty">
                      {t('deploy.packaging.computeEmpty')}
                    </span>
                  )}
              </div>
            </div>
          )}

          {recommendation && (
            <div className="ddp-pkg__reco">
              <span className="ddp-pkg__reco-badge">{t('deploy.packaging.recommendation')}</span>
              <span className="ddp-pkg__reco-value">
                {t(RUNTIME_LABEL[recommendation.runtime])} ·{' '}
                {t(QUANT_LABEL[recommendation.quantization])}
              </span>
              <span className="ddp-pkg__reco-why">{recommendation.rationale}</span>
            </div>
          )}

          <div className="ddp-pkg__grid">
            <Select
              id="pkg-runtime"
              labelText={t('deploy.packaging.field.runtime')}
              value={runtime}
              onChange={(e) => {
                setRuntime(e.target.value as PackagingRuntime);
                setOverridden(true);
              }}
            >
              {PACKAGING_RUNTIMES.map((v) => (
                <SelectItem key={v} value={v} text={t(RUNTIME_LABEL[v])} />
              ))}
            </Select>
            <Select
              id="pkg-quant"
              labelText={t('deploy.packaging.field.quantization')}
              value={quantization}
              onChange={(e) => {
                setQuantization(e.target.value as PackagingQuantization);
                setOverridden(true);
              }}
            >
              {PACKAGING_QUANTIZATIONS.map((v) => (
                <SelectItem key={v} value={v} text={t(QUANT_LABEL[v])} />
              ))}
            </Select>
            <NumberInput
              id="pkg-input"
              label={t('deploy.packaging.field.inputSize')}
              min={64}
              max={2048}
              step={32}
              value={inputSize}
              onChange={(_e, { value }) => {
                const n = Number(value);
                if (Number.isFinite(n)) setInputSize(Math.min(2048, Math.max(64, n)));
              }}
            />
          </div>

          <div className="ddp-pkg__actions">
            <Button size="md" disabled={!modelId || busy} onClick={submit}>
              {t('deploy.packaging.create')}
            </Button>
          </div>
        </div>
      )}

      {/* --- Liste des paquets --- */}
      <div className="ddp-pkg__list-head">
        <h3 className="ddp-pkg__subtitle">{t('deploy.packaging.packages')}</h3>
      </div>
      {!loading && packages.length === 0 ? (
        <p className="ddp-pkg__empty">{t('deploy.packaging.empty')}</p>
      ) : (
        <ul className="ddp-pkg__items">
          {packages.map((p) => {
            const meta = STATUS_META[p.status];
            const buildable = p.status === 'DRAFT' || p.status === 'FAILED';
            return (
              <li key={p.id} className="ddp-pkg__item">
                <div className="ddp-pkg__item-body">
                  <div className="ddp-pkg__item-name">
                    {p.modelName}
                    <span className="ddp-pkg__item-version"> · {p.modelVersion}</span>
                  </div>
                  <div className="ddp-pkg__item-meta">
                    {t(RUNTIME_LABEL[p.runtime])} · {t(QUANT_LABEL[p.quantization])} · {p.inputSize}
                    px
                    {p.targetAssetName ? ` · ${p.targetAssetName}` : ''}
                    {p.status === 'FAILED' && p.error ? ` · ${p.error}` : ''}
                  </div>
                </div>
                <span className={`ddp-pkg__status ddp-pkg__status--${meta.cls}`}>
                  <span className="ddp-pkg__status-dot" aria-hidden="true" />
                  {t(meta.key)}
                </span>
                <div className="ddp-pkg__item-actions">
                  {p.status === 'READY' && (
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Download}
                      iconDescription={t('deploy.packaging.download')}
                      hasIconOnly
                      onClick={() => download(p.id)}
                    />
                  )}
                  {canEdit && buildable && (
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Play}
                      iconDescription={t('deploy.packaging.build')}
                      hasIconOnly
                      onClick={() => build(p.id)}
                    />
                  )}
                  {canEdit && (
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      iconDescription={t('deploy.packaging.delete')}
                      hasIconOnly
                      onClick={() => remove(p.id)}
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
